import * as XLSX from 'xlsx';

export type PlAttachmentParsedRow = {
  package_no: string;
  order_no?: string | null;
  package_type_text?: string | null;
  net_weight_kg?: number | null;
  gross_weight_kg?: number | null;
  length_cm?: number | null;
  width_cm?: number | null;
  height_cm?: number | null;
  item_no?: string | null;
  supply_no?: string | null;
  description?: string | null;
  detail?: string | null;
  hs_code?: string | null;
  storage_instruction?: string | null;
  country_of_origin?: string | null;
  quantity?: number | null;
  unit?: string | null;
  iv_unit_price?: number | null;
  invoice_amount?: number | null;
  key_code?: string | null;
};

export type PackageItemDraft = {
  key: string;
  item_id?: number | null;
  parent_item_code?: string | null;
  itemcode?: string | null;
  size_text?: string | null;
  short_description?: string | null;
  long_description?: string | null;
  notes?: string | null;
  hs_code?: string | null;
  country_of_origin?: string | null;
  unit?: string | null;
  unit_price?: number | null;
  currency?: string | null;
  quantity?: number | null;
};

export const packageTypeOptions = [
  'box',
  'wooden box',
  'steel box',
  'bag',
  'crate',
  'steel crate',
  'bundle',
  'container 20f',
  'container 40f',
  'bare',
  'bulk',
  'skid',
  'special skid',
  // Note: backend check constraint currently includes literal 'container(SOC)can'
  'cage',
  'container(SOC)',
  'can',
  'case',
  'drum',
  'wooden drum',
  'wooden frame',
  'frame',
  'steel frame',
  'pallet',
  'rack',
  'roll',
  'saddle',
  'wrapped',
  'shrink wrapped',
  'tank',
  'wooden support',
  'steel support',
  'unpackedcarton',
  'other',
  'tbd',
] as const;

export type PackageType = (typeof packageTypeOptions)[number];

export function normalizePlPackageType(raw: string | null | undefined): PackageType | null {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return null;
  const exact = (packageTypeOptions as readonly string[]).find((x) => x.toLowerCase() === s);
  if (exact) return exact as PackageType;
  const has = (k: string) => s.includes(k);
  if (has('wood') && has('box')) return 'wooden box';
  if (has('steel') && has('box')) return 'steel box';
  if (has('box')) return 'box';
  if (has('steel') && has('crate')) return 'steel crate';
  if (has('crate')) return 'crate';
  if (has('bundle')) return 'bundle';
  if (has('bag')) return 'bag';
  if (has('pallet')) return 'pallet';
  if (has('rack')) return 'rack';
  if (has('drum')) return has('wood') ? 'wooden drum' : 'drum';
  if (has('frame')) return has('steel') ? 'steel frame' : 'frame';
  if (has('skid')) return has('special') ? 'special skid' : 'skid';
  if (has('container') && (has('20') || has('20f'))) return 'container 20f';
  if (has('container') && (has('40') || has('40f'))) return 'container 40f';
  if (has('shrink')) return 'shrink wrapped';
  if (has('wrapped')) return 'wrapped';
  if (has('tank')) return 'tank';
  if (has('bulk')) return 'bulk';
  if (has('bare')) return 'bare';
  return null;
}

export function getNextItemId(rows: PackageItemDraft[]) {
  const maxId = (rows || []).reduce((acc, r) => {
    const n = typeof r.item_id === 'number' ? r.item_id : Number(String(r.item_id ?? ''));
    if (!Number.isFinite(n)) return acc;
    return Math.max(acc, n);
  }, 0);
  return maxId + 1;
}

function toNumberOrNull(v: any) {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v));
  return Number.isFinite(n) ? n : null;
}

function toStringOrNull(v: any) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s ? s : null;
}

function toHsCodeStringOrNull(v: any) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const n = Number(s);
  if (Number.isFinite(n) && !s.includes('.') && !s.includes(',')) return String(Math.trunc(n));
  return s;
}

export async function parsePlAttachment(file: File): Promise<{ sheetName: string; rows: PlAttachmentParsedRow[] }> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array', cellDates: true });
  const sheetName =
    wb.SheetNames.find((n) => String(n).toLowerCase().includes('pl attachment')) ||
    wb.SheetNames.find((n) => String(n).toLowerCase().includes('pl')) ||
    wb.SheetNames[0];
  const ws = sheetName ? wb.Sheets[sheetName] : undefined;
  if (!ws) return { sheetName: sheetName || '', rows: [] };

  const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true }) as any[][];
  const norm = (x: any) => String(x ?? '').trim();
  const normHeader = (x: any) => norm(x).toLowerCase().replace(/\s+/g, ' ');

  let headerRowIdx = -1;
  for (let r = 0; r < Math.min(aoa.length, 80); r += 1) {
    const row = aoa[r] || [];
    if (row.some((c) => normHeader(c) === 'package no.' || normHeader(c) === 'package no')) {
      headerRowIdx = r;
      break;
    }
  }
  if (headerRowIdx < 0) {
    for (let r = 0; r < Math.min(aoa.length, 80); r += 1) {
      const row = aoa[r] || [];
      if (row.some((c) => normHeader(c).includes('country of origin'))) {
        headerRowIdx = r;
        break;
      }
    }
  }
  if (headerRowIdx < 0) return { sheetName, rows: [] };

  const h1 = (aoa[headerRowIdx] || []).map(norm);
  const h2 = (aoa[headerRowIdx + 1] || []).map(norm);

  const headerHints = [
    /^order no/,
    /^item no/,
    /^supply no/,
    /tag label/,
    /^country of origin/,
    /^hs code/,
    /^invoice amount/,
    /\$\/pc/,
    /^key code/,
    /^dimension/,
    /^freight ton/,
    /^net w'?t/,
    /^gross w'?t/,
    /^volume/,
  ];
  const h2Score = (h2 || []).reduce((acc, cell) => {
    const s = normHeader(cell);
    if (!s) return acc;
    return acc + (headerHints.some((re) => re.test(s)) ? 1 : 0);
  }, 0);
  const hasSecondHeaderRow = h2Score >= 2;

  const headers = h1.map((a, i) => {
    const b = hasSecondHeaderRow ? (h2[i] || '') : '';
    const aa = a.trim();
    const bb = String(b || '').trim();
    if (aa && bb && aa !== bb) return `${aa} ${bb}`;
    return aa || bb || '';
  });

  const findCol = (re: RegExp) => headers.findIndex((h) => re.test(normHeader(h)));

  const idxPackage = findCol(/^package no/);
  const idxOrder = findCol(/^order no/) >= 0 ? findCol(/^order no/) : findCol(/^purchase/);
  const idxItemNo = findCol(/^item no/);
  const idxSupplyNo = findCol(/^supply no/) >= 0 ? findCol(/^supply no/) : findCol(/tag label/);
  const idxDesc = findCol(/^description/);
  const idxDetail = findCol(/^detail/);
  const idxQty = findCol(/quant/);
  const idxHs = findCol(/^hs code/);
  const idxStorage = findCol(/^storage instruction/);
  const idxOrigin = findCol(/^country of origin/);
  const idxIvUnit = findCol(/^iv unit/) >= 0 ? findCol(/^iv unit/) : findCol(/\$\/pc/);
  const idxInvAmt = findCol(/^invoice amount/) >= 0 ? findCol(/^invoice amount/) : findCol(/^invoice amount/);
  const idxKeyCode = findCol(/^key code/);
  const idxPackStyleHeader = findCol(/nos\. of p'?kg|pack['’]g style|pack'g style|packing style|packg style/i);
  const idxDim = findCol(/^dimension/);
  const idxNetWt = findCol(/^net w'?t/);
  const idxGrossWt = findCol(/^gross w'?t/);
  const idxL = findCol(/\bdimension\b.*\bl\b/) >= 0 ? findCol(/\bdimension\b.*\bl\b/) : findCol(/^l$/);
  const idxW = findCol(/\bdimension\b.*\bw\b/) >= 0 ? findCol(/\bdimension\b.*\bw\b/) : findCol(/^w$/);
  const idxH = findCol(/\bdimension\b.*\bh\b/) >= 0 ? findCol(/\bdimension\b.*\bh\b/) : findCol(/^h$/);

  let idxQtyUnit = -1;
  if (idxQty >= 0) {
    const hNext = headers[idxQty + 1] ? normHeader(headers[idxQty + 1]) : '';
    if (!hNext) idxQtyUnit = idxQty + 1;
    else if (idxHs === idxQty + 2) idxQtyUnit = idxQty + 1;
  }

  const startRowCandidate = headerRowIdx + (hasSecondHeaderRow ? 2 : 1);
  let startRow = startRowCandidate;
  for (let r = startRowCandidate; r < Math.min(aoa.length, startRowCandidate + 50); r += 1) {
    const row = aoa[r] || [];
    const pkg = idxPackage >= 0 ? toStringOrNull(row[idxPackage]) : null;
    const itemNo = idxItemNo >= 0 ? toStringOrNull(row[idxItemNo]) : null;
    const supplyNo = idxSupplyNo >= 0 ? toStringOrNull(row[idxSupplyNo]) : null;
    const desc = idxDesc >= 0 ? toStringOrNull(row[idxDesc]) : null;
    const detail = idxDetail >= 0 ? toStringOrNull(row[idxDetail]) : null;
    if (pkg || itemNo || supplyNo || desc || detail) {
      startRow = r;
      break;
    }
  }

  const out: PlAttachmentParsedRow[] = [];
  for (let r = startRow; r < aoa.length; r += 1) {
    const row = aoa[r] || [];
    const pkg = idxPackage >= 0 ? toStringOrNull(row[idxPackage]) : null;
    const itemNo = idxItemNo >= 0 ? toStringOrNull(row[idxItemNo]) : null;
    const supplyNo = idxSupplyNo >= 0 ? toStringOrNull(row[idxSupplyNo]) : null;
    const desc = idxDesc >= 0 ? toStringOrNull(row[idxDesc]) : null;
    const detail = idxDetail >= 0 ? toStringOrNull(row[idxDetail]) : null;

    const pkgNorm = String(pkg ?? '').trim();
    const isTotalLike = pkgNorm && /^total$/i.test(pkgNorm);
    const hasItemCore = Boolean(itemNo || supplyNo || desc || detail);
    if (isTotalLike) continue;
    if (!pkgNorm) continue;
    if (!hasItemCore) continue;

    const qty = idxQty >= 0 ? toNumberOrNull(row[idxQty]) : null;
    const unit = idxQtyUnit >= 0 ? toStringOrNull(row[idxQtyUnit]) : null;
    const hs = idxHs >= 0 ? toHsCodeStringOrNull(row[idxHs]) : null;
    const origin = idxOrigin >= 0 ? toStringOrNull(row[idxOrigin]) : null;
    const storage = idxStorage >= 0 ? toStringOrNull(row[idxStorage]) : null;
    const unitPrice = idxIvUnit >= 0 ? toNumberOrNull(row[idxIvUnit]) : null;
    const invAmt = idxInvAmt >= 0 ? toNumberOrNull(row[idxInvAmt]) : null;
    const orderNo = idxOrder >= 0 ? toStringOrNull(row[idxOrder]) : null;
    const keyCode = idxKeyCode >= 0 ? toStringOrNull(row[idxKeyCode]) : null;

    const pkgCountOrStyle = idxPackStyleHeader >= 0 ? toStringOrNull(row[idxPackStyleHeader]) : null;
    const pkgStyleNext =
      idxPackStyleHeader >= 0 && idxPackStyleHeader + 1 < headers.length && !normHeader(headers[idxPackStyleHeader + 1])
        ? toStringOrNull(row[idxPackStyleHeader + 1])
        : null;
    const pkgTypeText = pkgStyleNext || pkgCountOrStyle;

    const hasMmUnit =
      [idxDim, idxL, idxW, idxH]
        .filter((i) => i >= 0)
        .some((i) => normHeader(headers[i]).includes('mm')) || false;
    const dimConv = hasMmUnit ? 0.1 : 1;
    const lRaw = idxL >= 0 ? toNumberOrNull(row[idxL]) : null;
    const wRaw = idxW >= 0 ? toNumberOrNull(row[idxW]) : null;
    const hRaw = idxH >= 0 ? toNumberOrNull(row[idxH]) : null;

    out.push({
      package_no: pkgNorm,
      order_no: orderNo,
      package_type_text: pkgTypeText,
      net_weight_kg: idxNetWt >= 0 ? toNumberOrNull(row[idxNetWt]) : null,
      gross_weight_kg: idxGrossWt >= 0 ? toNumberOrNull(row[idxGrossWt]) : null,
      length_cm: lRaw != null ? lRaw * dimConv : null,
      width_cm: wRaw != null ? wRaw * dimConv : null,
      height_cm: hRaw != null ? hRaw * dimConv : null,
      item_no: itemNo,
      supply_no: supplyNo,
      description: desc,
      detail,
      hs_code: hs,
      storage_instruction: storage,
      country_of_origin: origin,
      quantity: qty,
      unit,
      iv_unit_price: unitPrice,
      invoice_amount: invAmt,
      key_code: keyCode,
    });
  }

  return { sheetName, rows: out };
}

export function buildItemsFromPlRows(rows: PlAttachmentParsedRow[], baseItemId: number) {
  let next = baseItemId;
  const mapped: PackageItemDraft[] = [];
  (rows || []).forEach((r) => {
    const notesParts: string[] = [];
    if (r.order_no) notesParts.push(`ORDER: ${r.order_no}`);
    if (r.supply_no) notesParts.push(`SUPPLY: ${r.supply_no}`);
    if (r.storage_instruction) notesParts.push(`STORAGE: ${r.storage_instruction}`);
    if (r.key_code) notesParts.push(`KEY: ${r.key_code}`);
    if (r.invoice_amount != null) notesParts.push(`INV_AMT: ${Number(r.invoice_amount).toFixed(3)}`);

    const item: PackageItemDraft = {
      key: `pl_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      item_id: next,
      // Swap mapping per template meaning:
      // - Supply No. (Tag Label No.) -> itemcode
      // - Item No. (Equip. No.)     -> parent_item_code
      parent_item_code: r.item_no ?? null,
      itemcode: r.supply_no ?? null,
      size_text: null,
      short_description: r.description ?? null,
      long_description: r.detail ?? null,
      notes: notesParts.length ? notesParts.join(' | ') : null,
      hs_code: r.hs_code ?? null,
      country_of_origin: r.country_of_origin ?? null,
      unit: r.unit ?? 'PCS',
      unit_price: r.iv_unit_price ?? null,
      currency: 'USD',
      quantity: r.quantity ?? null,
    };

    const hasAny =
      item.parent_item_code !== null ||
      item.itemcode !== null ||
      item.short_description !== null ||
      item.long_description !== null ||
      item.notes !== null ||
      item.hs_code !== null ||
      item.country_of_origin !== null ||
      item.unit !== null ||
      item.unit_price !== null ||
      item.quantity !== null;
    if (!hasAny) return;

    next += 1;
    mapped.push(item);
  });
  return mapped;
}

export type BuiltPackage = {
  package_id: string;
  po_number?: string | null;
  package_type_text?: string | null;
  package_type?: PackageType | null;
  net_weight_kg?: number | null;
  gross_weight_kg?: number | null;
  length_cm?: number | null;
  width_cm?: number | null;
  height_cm?: number | null;
  items: PackageItemDraft[];
  rows: PlAttachmentParsedRow[];
};

export function buildPackagesFromParsedRows(parsed: PlAttachmentParsedRow[]): BuiltPackage[] {
  const byPid = new Map<string, PlAttachmentParsedRow[]>();
  (parsed || []).forEach((r) => {
    const pid = String(r.package_no || '').trim();
    if (!pid) return;
    const arr = byPid.get(pid) || [];
    arr.push(r);
    byPid.set(pid, arr);
  });

  const out: BuiltPackage[] = [];
  byPid.forEach((rows, pid) => {
    const first = rows[0];
    const pkgTypeText = first?.package_type_text ?? null;
    const pkgType = normalizePlPackageType(pkgTypeText);
    out.push({
      package_id: pid,
      po_number: first?.order_no ?? null,
      package_type_text: pkgTypeText,
      package_type: pkgType,
      net_weight_kg: first?.net_weight_kg ?? null,
      gross_weight_kg: first?.gross_weight_kg ?? null,
      length_cm: first?.length_cm ?? null,
      width_cm: first?.width_cm ?? null,
      height_cm: first?.height_cm ?? null,
      items: buildItemsFromPlRows(rows, 1),
      rows,
    });
  });
  return out;
}

